import { compactNumber } from "@/lib/formatNumber";
import { publicOrigin } from "@/lib/publicOrigin";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { prefersNativeHls, mseSupported, loadHls } from "@/lib/videoSource";
import { Radio, Eye, Play, Share2, MapPin, ChevronRight, Star, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { shareLinkCard } from "@/lib/nativeShare";
import { useApp } from "@/context/AppContext";
import { liveTitle } from "@/lib/liveTitle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveMediaUrl } from "@/components/FileUpload";
import { avatarSrc } from "@/lib/defaults";
import { DismissX } from "@/components/smart/DismissX";

// Single source of truth for "who is live right now": all current broadcasts (churches
// AND creators), augmented with a distance ("près de toi") when GPS is already granted.
export const useLiveNow = (enabled = true) => {
  const [lives, setLives] = useState([]);
  const [coords, setCoords] = useState(null);
  const [permState, setPermState] = useState(null); // "granted" | "prompt" | "denied"
  const [hasNearby, setHasNearby] = useState(false);
  const [tick, setTick] = useState(0); // bumps each refresh to bust the live-thumbnail cache
  const [loading, setLoading] = useState(true);

  const getPos = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }, []);

  // Only auto-fetch location if the user ALREADY granted it — never surprise-prompt on the feed.
  useEffect(() => {
    let cancelled = false;
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" })
        .then((st) => {
          if (cancelled) return;
          setPermState(st.state);
          if (st.state === "granted") getPos();
          st.onchange = () => { setPermState(st.state); if (st.state === "granted") getPos(); };
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [getPos]);

  const load = useCallback(() => {
    const base = api.get("/live", { params: { status: "live" } });
    const near = coords
      ? api.get("/live", { params: { status: "live", lat: coords.lat, lon: coords.lon } }).catch(() => ({ data: { lives: [] } }))
      : Promise.resolve({ data: { lives: [] } });
    Promise.all([base, near]).then(([{ data }, { data: nd }]) => {
      const distById = {};
      const geoById = {};
      (nd.lives || []).forEach((l) => {
        if (l.distance_km != null) distById[l.id] = l.distance_km;
        if (l.church_lat != null && l.church_lon != null) geoById[l.id] = { lat: l.church_lat, lon: l.church_lon };
      });
      // Lot 557 — pertinence : mes églises / abonnements (serveur : mine) → près de moi (distance) → les plus
      // regardés puis les plus récents (ordre serveur).
      const tier = (l) => (l.mine ? 0 : l._distance != null ? 1 : 2);
      const merged = (data.lives || [])
        .map((l, i) => ({ ...l, _distance: distById[l.id], _lat: geoById[l.id]?.lat, _lon: geoById[l.id]?.lon, _i: i }))
        .sort((a, b) => (tier(a) - tier(b)) || (tier(a) === 1 ? (a._distance - b._distance) : 0) || a._i - b._i);
      setLives(merged);
      setHasNearby(merged.some((l) => l._distance != null));
      setTick((n) => n + 1);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [coords]);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    let interval = null;
    const start = () => { if (!interval) interval = setInterval(load, 20000); }; // near real-time viewer counts
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVis = () => { if (document.visibilityState === "visible") { load(); start(); } else { stop(); } };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [load, enabled]);

  return { lives, tick, hasNearby, coords, permState, getPos, loading };
};

// Muted, low-latency HLS preview that only mounts while the card is hovered — gives
// a live glimpse of the broadcast to entice viewers to join. Destroyed on unmount.
const LiveHoverVideo = ({ src }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !src) return;
    el.muted = true;
    let hls;
    let gone = false;
    const onPlaying = () => setPlaying(true);
    el.addEventListener("playing", onPlaying);
    if (prefersNativeHls(el)) {
      // Native HLS (Safari / iOS)
      el.src = src;
      el.play().catch(() => {});
    } else if (mseSupported()) {
      loadHls().then((Hls) => {
        if (gone) return;
        hls = new Hls({ backBufferLength: 30, lowLatencyMode: true });
        hls.loadSource(src);
        hls.attachMedia(el);
        hls.on(Hls.Events.MANIFEST_PARSED, () => el.play().catch(() => {}));
        // Recover instead of giving up — a hover preview should be resilient.
        hls.on(Hls.Events.ERROR, (_e, d) => {
          if (!d?.fatal) return;
          if (d.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (d.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
          else { try { hls.destroy(); } catch { /* noop */ } }
        });
      }).catch(() => {});
    }
    return () => { gone = true; el.removeEventListener("playing", onPlaying); try { hls?.destroy(); } catch { /* noop */ } };
  }, [src]);
  return (
    <video
      ref={ref}
      muted
      playsInline
      autoPlay
      loop
      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
      style={{ opacity: playing ? 1 : 0 }}
      data-testid="live-hover-video"
    />
  );
};

// One live card. `variant` = "carousel" (fixed width, feed) | "half" (2 visibles, fil 2-3 lives) | "grid" (compacte,
// grille 2 colonnes mobile) | "list" (full width, dedicated page).
export const LiveNowCard = ({ l, tick = 0, variant = "carousel" }) => {
  const { t } = useApp();
  const [hovering, setHovering] = useState(false);
  const streamThumb = l.thumbnail_url || l.embed?.thumbnail;
  const cover = streamThumb ? `${streamThumb}${streamThumb.includes("?") ? "&" : "?"}_t=${tick}` : null;
  const name = l.church_name || l.host?.name;
  // Only preview on hover-capable (non-touch) devices, and only when a live HLS is available.
  const hoverCapable = typeof window !== "undefined" && window.matchMedia?.("(hover: hover)")?.matches;
  const hlsSrc = l.hls_url ? resolveMediaUrl(l.hls_url) : null;
  const compact = variant === "half" || variant === "grid";
  const wrap = {
    carousel: "group block snap-start shrink-0 w-[78vw] max-w-[300px] rounded-2xl overflow-hidden border transition-transform hover:scale-[1.01]",
    featured: "group block w-full sm:w-[360px] rounded-2xl overflow-hidden border transition-transform hover:scale-[1.01]",
    half: "group block snap-start shrink-0 w-[calc(50%-0.375rem)] min-w-[140px] rounded-2xl overflow-hidden border transition-transform hover:scale-[1.01]",
    grid: "group block w-full rounded-2xl overflow-hidden border transition-transform hover:scale-[1.01]",
    list: "group block w-full rounded-2xl overflow-hidden border transition-transform hover:scale-[1.01]",
  }[variant] || "group block w-full rounded-2xl overflow-hidden border";
  const mineLabel = l.mine === "church" ? t("live.mine_church", "Mon église") : l.mine === "host" ? t("live.mine_host", "Suivi(e)") : null;
  return (
    <Link
      to={`/live?watch=${l.id}`}
      className={wrap}
      style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}
      data-testid={`live-tile-${l.id}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div className="relative aspect-[16/9] bg-black">
        {cover ? (
          <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid={`live-thumb-${l.id}`} />
        ) : (
          <img src={resolveMediaUrl(l.host?.avatar_url)} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-md opacity-70" />
        )}
        {hovering && hoverCapable && hlsSrc && <LiveHoverVideo src={hlsSrc} />}
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(0,0,0,0.10), rgba(0,0,0,0.66))" }} />
        {!cover && (
          <div className="absolute inset-0 grid place-items-center">
            <Avatar className="h-14 w-14 shadow-lg ring-2 ring-white/85 transition-transform group-hover:scale-105">
              <AvatarImage src={avatarSrc(l.host)} />
              <AvatarFallback>{l.host?.name?.[0]}</AvatarFallback>
            </Avatar>
          </div>
        )}
        {cover && (
          <Avatar className="absolute bottom-2 left-2 h-8 w-8 ring-2 ring-white shadow">
            <AvatarImage src={avatarSrc(l.host)} />
            <AvatarFallback>{l.host?.name?.[0]}</AvatarFallback>
          </Avatar>
        )}
        <span className="absolute top-2 left-2 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white shadow" style={{ background: "#e11d48" }}>
          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> {compact ? t("live.badge_short", "LIVE") : t("embed.live_badge", "EN DIRECT")}
        </span>
        {!compact && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); const lt = liveTitle(l, t, { name }); shareLinkCard({ title: lt, text: `${l.title && l.host?.name ? l.host.name + " — " : ""}${lt} 🔴`, url: `${publicOrigin()}/live?watch=${l.id}`, copiedMessage: t("live.invite_copied", "Lien du direct copié 🔗") }); }}
          className="absolute top-2 right-2 h-7 w-7 rounded-full grid place-items-center bg-black/55 text-white hover:bg-black/80 transition-colors"
          data-testid={`live-invite-${l.id}`}
          title={t("live.invite", "Inviter")}
          aria-label={t("live.invite", "Inviter")}
        >
          <Share2 size={13} />
        </button>
        )}
        <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid={`live-viewers-${l.id}`}>
          <Eye size={11} /> {compactNumber(l.viewers ?? 0)}
        </span>
      </div>
      <div className={compact ? "px-2 py-1.5" : "px-2.5 py-2"}>
        <div className={`${compact ? "text-xs" : "text-sm"} font-semibold truncate`} data-testid={`live-card-title-${l.id}`}>{liveTitle(l, t, { name })}</div>
        <div className="text-xs truncate flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}>
          {mineLabel && (
            <span className="shrink-0 rounded-full px-1.5 py-px text-[10px] font-bold" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`live-mine-${l.id}`}>{mineLabel}</span>
          )}
          <span className="truncate">{name}</span>
          {l._distance != null && (
            <span className="inline-flex items-center gap-0.5 shrink-0 font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid={`live-dist-${l.id}`}>
              <MapPin size={10} /> {t("live.km_away", "à {n} km").replace("{n}", String(l._distance).replace(".", ","))}
            </span>
          )}
        </div>
        <div
          className={`${compact ? "mt-1.5 py-1 text-[11px]" : "mt-2 py-1.5 text-xs"} flex items-center justify-center gap-1.5 rounded-full font-bold text-white transition-transform group-hover:scale-[1.02]`}
          style={{ background: "#e11d48", boxShadow: "0 0 0 0 rgba(225,29,72,0.6)", animation: compact ? undefined : "yalolaLivePulse 1.8s ease-out infinite" }}
          data-testid={`live-join-cta-${l.id}`}
        >
          <Play size={compact ? 11 : 13} className="fill-white" /> {compact ? t("live.join_short", "Rejoindre") : t("live.join", "Rejoindre le direct")}
        </div>
      </div>
    </Link>
  );
};

// Lot 557 — bulle « story en direct » : avatar cerclé de rouge, badge LIVE, nom, spectateurs. Pour les rangées de 4+ lives.
export const LiveBubble = ({ l }) => {
  const { t } = useApp();
  const name = l.church_name || l.host?.name || "";
  return (
    <Link to={`/live?watch=${l.id}`} className="snap-start shrink-0 w-[76px] flex flex-col items-center gap-1 text-center group" data-testid={`live-bubble-${l.id}`} title={liveTitle(l, t, { name })}>
      <span className="relative">
        <span className="block h-16 w-16 rounded-full p-[2.5px] transition-transform group-hover:scale-105 group-active:scale-95" style={{ background: "conic-gradient(from 180deg, #e11d48, #fb7185, #e11d48)" }}>
          <Avatar className="h-full w-full ring-2" style={{ "--tw-ring-color": "var(--yalola-surface)" }}>
            <AvatarImage src={avatarSrc(l.host)} />
            <AvatarFallback>{name?.[0]}</AvatarFallback>
          </Avatar>
        </span>
        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-0.5 rounded px-1.5 py-px text-[9px] font-extrabold uppercase text-white shadow" style={{ background: "#e11d48" }}>
          <span className="h-1 w-1 rounded-full bg-white animate-pulse" /> {t("live.badge_short", "LIVE")}
        </span>
        {l.mine && <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full grid place-items-center text-white" style={{ background: "var(--yalola-primary)" }} aria-label={l.mine === "church" ? t("live.mine_church", "Mon église") : t("live.mine_host", "Suivi(e)")}><Star size={9} className="fill-white" /></span>}
        {/* Lot 706 — direct boosté avec des points : fusée en haut à gauche */}
        {l.boosted && <span className="absolute -top-0.5 -left-0.5 h-4 w-4 rounded-full grid place-items-center text-white" style={{ background: "#0F766E" }} aria-label={t("live.boosted", "Boosté")} data-testid={`live-boosted-${l.id}`}><Rocket size={9} /></span>}
      </span>
      <span className="text-[11px] font-semibold truncate w-full leading-tight mt-0.5" data-testid={`live-bubble-name-${l.id}`}>{name}</span>
      <span className="flex items-center gap-0.5 text-[10px] tabular-nums" style={{ color: "var(--yalola-text-muted)" }}><Eye size={10} /> {compactNumber(l.viewers ?? 0)}</span>
    </Link>
  );
};

// Live section header (pulsing red dot + "En direct" / "près de toi" + count).
const LiveHeaderLabel = ({ hasNearby, count }) => {
  const { t } = useApp();
  return (
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide whitespace-nowrap min-w-0" style={{ color: "#e11d48" }}>
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: "#e11d48" }} />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: "#e11d48" }} />
      </span>
      <Radio size={14} /> {hasNearby ? t("live.churches_near", "Directs près de toi") : t("live.now", "En direct")}
      <span className="text-[10px] font-semibold" style={{ color: "var(--yalola-text-muted)" }}>({count})</span>
    </div>
  );
};

// Feed widget (lot 557, adaptatif) : 1 live → grande carte ; 2-3 → cartes à mi-largeur (2 visibles) ; 4+ → le plus
// pertinent en grande carte + rangée de bulles « en direct » avec tous les autres (jusqu'à 20) + « Voir tout » → /lives.
export const LiveNowWidget = ({ data = null, onDismiss = null }) => {
  const { t, config } = useApp();
  const own = useLiveNow(!data);
  const { lives, tick, hasNearby, coords, permState, getPos } = data || own;

  if (lives.length === 0) return null;
  const n = lives.length;
  const mode = n === 1 ? "single" : n <= 3 ? "half" : "bubbles";
  const bubbleLimit = Math.max(4, Number(config?.settings?.live_bubbles_limit) || 20);
  const bubbles = mode === "bubbles" ? lives.slice(1, 1 + bubbleLimit) : [];
  const hasMore = mode === "bubbles" ? n > 1 + bubbleLimit : false;
  const canEnableLoc = !coords && permState && permState !== "granted" && !!navigator.geolocation;

  return (
    <div className="yalola-card p-4 relative overflow-hidden" data-testid="live-now-widget" data-mode={mode} data-count={n}>
      <span className="pointer-events-none absolute -top-10 -right-10 h-28 w-28 rounded-full opacity-[0.18] animate-pulse" style={{ background: "#e11d48" }} aria-hidden />
      <div className="relative flex items-center justify-between gap-2 mb-3">
        <LiveHeaderLabel hasNearby={hasNearby} count={n} />
        <div className="flex items-center gap-1 shrink-0">
        {canEnableLoc ? (
          <button
            type="button"
            onClick={getPos}
            className="flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors shrink-0"
            style={{ color: "var(--yalola-primary)", borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" }}
            data-testid="live-enable-location"
          >
            <MapPin size={12} /> <span className="truncate max-w-[150px]">{t("live.enable_location", "Voir les directs près de toi")}</span>
          </button>
        ) : n > 1 ? (
          <Link to="/lives" className="inline-flex items-center gap-0.5 text-xs font-semibold shrink-0" style={{ color: "var(--yalola-primary)" }} data-testid="live-now-see-all">
            {t("common.see_all", "Voir tout")} ({n}) <ChevronRight size={14} />
          </Link>
        ) : null}
        <DismissX onClick={onDismiss} testId="live-now-dismiss" label={t("smart_cards.dismiss", "Masquer")} />
        </div>
      </div>

      {mode === "single" && (
        <div className="relative flex gap-3" data-testid="live-now-carousel">
          <LiveNowCard l={lives[0]} tick={tick} variant="featured" />
        </div>
      )}
      {mode === "half" && (
        <div className="relative flex gap-3 overflow-x-auto pb-1.5 -mx-1 px-1 snap-x hide-scrollbar" data-testid="live-now-carousel">
          {lives.map((l) => <LiveNowCard key={l.id} l={l} tick={tick} variant="half" />)}
        </div>
      )}
      {mode === "bubbles" && (
        <>
          <div className="relative flex gap-3" data-testid="live-now-featured">
            <LiveNowCard l={lives[0]} tick={tick} variant="featured" />
          </div>
          <div className="relative mt-3 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }} data-testid="live-now-others-label">
              {t("live.others_now", "{n} autres directs").replace("{n}", String(n - 1))}
            </span>
          </div>
          <div className="relative mt-2 flex gap-2.5 overflow-x-auto pb-1.5 -mx-1 px-1 snap-x hide-scrollbar" data-testid="live-now-bubbles">
            {bubbles.map((l) => <LiveBubble key={l.id} l={l} />)}
            <Link to="/lives" className="snap-start shrink-0 w-[76px] flex flex-col items-center gap-1 text-center" data-testid="live-now-see-all-tile">
              <span className="h-16 w-16 rounded-full grid place-items-center border-2 border-dashed" style={{ borderColor: "var(--yalola-primary)", color: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" }}><ChevronRight size={22} /></span>
              <span className="text-[11px] font-semibold leading-tight mt-0.5" style={{ color: "var(--yalola-primary)" }}>{t("common.see_all", "Voir tout")}{hasMore ? ` (${n})` : ""}</span>
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export { LiveHeaderLabel };
export default LiveNowWidget;
